/* ==========================================================================
   Kankatala Custom — shared JS for custom sections
   Covers: Kankatala Collections (scroll-carousel autoplay), Kankatala Stores
   (accordion + gallery reparenting), Kankatala Luxury Grid (click-to-expand
   fan), and Kankatala Occasion Grid (Splide.js slider). Runs generically
   across every instance of each section that appears on the page, rather
   than being tied to one specific section id, since this file is loaded as
   a static asset (no Liquid).
   ========================================================================== */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ------------------------------------------------------------------------
     Shared autoplay for any scroll-carousel with data-kankatala-autoplay
     (used by Kankatala Collections). scroll-carousel has no built-in
     autoplay attribute, so this drives it via its public .next()/.select().
  ------------------------------------------------------------------------ */
  function initKankatalaAutoplayCarousels() {
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.querySelectorAll('scroll-carousel[data-kankatala-autoplay]').forEach(function (carousel) {
      if (carousel.dataset.kankatalaAutoplayInit) return;
      carousel.dataset.kankatalaAutoplayInit = 'true';

      if (reducedMotion) {
        console.warn('[kankatala] Autoplay skipped on', carousel, '— the browser/OS has "prefers-reduced-motion" enabled.');
        return;
      }

      if (typeof carousel.next !== 'function' || typeof carousel.select !== 'function') {
        console.warn('[kankatala] Autoplay skipped on', carousel, '— scroll-carousel custom element has no .next()/.select() yet (not upgraded?).');
        return;
      }

      var cellCount = carousel.cells ? carousel.cells.length : 0;
      if (cellCount <= 1) {
        console.warn('[kankatala] Autoplay set up on', carousel, 'but it only sees', cellCount, 'cell(s) — nothing to scroll to. Check "Items per row" isn\'t \u2265 the number of blocks, and that items aren\'t being filtered out as [hidden].');
      }

      var seconds = parseFloat(carousel.getAttribute('data-kankatala-autoplay')) || 5;
      // Default to looping when the attribute isn't present at all (this
      // section's current "Stack products" version has no loop setting,
      // so it never emits this attribute) — only an explicit "false"
      // value turns looping off.
      var loopEnabled = carousel.getAttribute('data-kankatala-loop') !== 'false';
      console.info('[kankatala] Autoplay running on', carousel, '—', cellCount, 'cell(s), every', seconds, 'sec, loop:', loopEnabled, '.');
      var timer = null;

      function tick() {
        // ScrollCarousel.next() clamps at the last cell and never loops back —
        // calling it again once at the end is a silent no-op forever. When
        // "Enable carousel loop" is on, loop back to the start manually once
        // we're there; when it's off, stop autoplay entirely at the end
        // instead (e.g. resting on the View All card) rather than looping.
        if (!carousel.cells || carousel.cells.length === 0) return;
        var atEnd = carousel.selectedIndex >= carousel.cells.length - 1;
        if (atEnd) {
          if (loopEnabled && typeof carousel.select === 'function') {
            carousel.select(0);
          } else {
            stop();
          }
        } else if (typeof carousel.next === 'function') {
          carousel.next();
        }
      }

      function start() {
        stop();
        timer = setInterval(tick, seconds * 1000);
      }
      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }

      start();
      carousel.addEventListener('mouseenter', stop);
      carousel.addEventListener('mouseleave', start);
      carousel.addEventListener('focusin', stop);
      carousel.addEventListener('focusout', start);
    });
  }

  /* ------------------------------------------------------------------------
     Kankatala Stores — slider of store cards (Splide).
     Every store from every state sits in ONE flat slider, in state order.
     It always moves ONE store at a time (perMove: 1).
       Desktop: Cards in view at once (2 on tablets), with a position label
                that follows the slider: "1–4 of 12 stores", then "2–5", ...
       Mobile:  one store at a time, plus a tab per state: tapping a tab jumps
                to that state's first store, and the active tab follows the
                slider as it moves.
     Options come from data attributes set in kankatala-stores.liquid (loop,
     autoplay seconds, cards per page). Loop is decided separately for each
     breakpoint and only when there are MORE stores than fit in one view there,
     because type: 'loop' breaks once perPage reaches the slide count; with the
     setting on but too few stores it falls back to plain 'slide' + rewind, and
     with the setting off it simply stops at the last store.
     Reuses the same self-loading Splide mechanism as the Occasion Grid
     (see loadKkSplideAssets) — see the comment there for why that's needed.
  ------------------------------------------------------------------------ */
  function initKankatalaStores() {
    var roots = document.querySelectorAll('[data-kk-stores-splide]');
    if (roots.length === 0) return;

    var jsUrl = roots[0].getAttribute('data-splide-js-src');
    var cssUrl = roots[0].getAttribute('data-splide-css-href');

    loadKkSplideAssets(jsUrl, cssUrl).then(function () {
      if (typeof window.Splide === 'undefined') {
        console.warn('[kankatala] Splide library still not available after attempting to load it — check that splide.min.js is actually in assets/ and not 0 bytes.');
        return;
      }

      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      roots.forEach(function (el) {
        if (el.dataset.kkStoresInit) return;
        el.dataset.kkStoresInit = 'true';

        var section = el.closest('.kankatala-stores');
        // Real slides, read BEFORE mounting (mounting adds clones when looping).
        var realSlides = Array.prototype.slice.call(el.querySelectorAll('.splide__slide'));
        var slideCount = realSlides.length;
        if (slideCount === 0) return;

        var perPageDesktop = Math.max(1, parseInt(el.getAttribute('data-per-page-desktop'), 10) || 4);
        var perPageTablet = Math.min(2, perPageDesktop);
        var perPageMobile = Math.min(3, Math.max(1, parseInt(el.getAttribute('data-items-mobile'), 10) || 1));
        var peekMobile = parseFloat(el.getAttribute('data-peek-mobile')) || 0;
        var loopRequested = el.getAttribute('data-loop') !== 'false';
        var autoplaySeconds = parseFloat(el.getAttribute('data-autoplay-seconds')) || 0;
        var hasArrows = !!(el.querySelector('.splide__arrow--prev') && el.querySelector('.splide__arrow--next'));

        // One breakpoint's worth of options. Splide applies only the SINGLE
        // closest matching breakpoint on top of the base options (they don't
        // stack), so each entry has to carry everything that differs.
        function layout(perPage, gap) {
          var canLoop = loopRequested && slideCount > perPage;
          return { type: canLoop ? 'loop' : 'slide', rewind: loopRequested && !canLoop, perPage: perPage, gap: gap };
        }

        var base = layout(perPageDesktop, '1.25rem');
        var options = {
          type: base.type,
          rewind: base.rewind,
          perPage: base.perPage,
          // ONE store per arrow click / swipe / auto-scroll step, however many
          // are in view (without this Splide moves a whole page at a time).
          // Set on the base options only: breakpoints inherit it.
          perMove: 1,
          gap: base.gap,
          arrows: hasArrows,
          pagination: false,
          autoplay: !!(autoplaySeconds && !reducedMotion),
          interval: autoplaySeconds ? autoplaySeconds * 1000 : undefined,
          pauseOnHover: true,
          pauseOnFocus: true,
          breakpoints: {
            999: layout(perPageTablet, '1.25rem'),
            699: layout(perPageMobile, '1rem')
          }
        };

        // Mobile "peek": whatever Items per row is (1, 2 or 3), reserve room on
        // the right of the track so a fraction f of the NEXT store shows beside
        // the N full ones (33% = one third) — a cue that there is more to swipe.
        // With N full slides of width s, gap g, track width C and right padding
        // p, the slides fill C - p = N*s + (N-1)*g and the next slide starts a
        // gap after that, so its visible part is p - g. Making that a fraction
        // f of s and solving for p gives
        //     p = (f*C + g*(N*(1-f) + f)) / (N + f)
        // i.e. calc((100*f/(N+f))% + (g*(N*(1-f)+f)/(N+f))rem), with g being the
        // 1rem mobile gap below. Same maths as the Occasion Grid. Only applies
        // when there IS a next store to peek at.
        if (peekMobile > 0 && slideCount > perPageMobile) {
          var f = Math.min(peekMobile, 60) / 100;
          var n = perPageMobile;
          var peekPct = (100 * f / (n + f)).toFixed(3);
          var peekGapRem = ((n * (1 - f) + f) / (n + f)).toFixed(3);
          var peekPad = 'calc(' + peekPct + '% + ' + peekGapRem + 'rem)';
          options.breakpoints[699].padding = { right: peekPad };
          // The mobile arrows float over the first card's photo, so CSS needs
          // the same value to stop them short of the peeking card.
          el.style.setProperty('--kk-stores-peek-pad', peekPad);
        } else {
          el.style.removeProperty('--kk-stores-peek-pad');
        }

        if (loopRequested && (slideCount <= perPageDesktop || slideCount <= perPageTablet || slideCount <= perPageMobile)) {
          console.info('[kankatala] Stores slider: loop is on but', slideCount, 'store(s) isn\'t more than fits in one view at some screen sizes (desktop', perPageDesktop, '/ tablet', perPageTablet, '/ mobile', perPageMobile, ') — those sizes scroll to the end instead of looping.');
        }
        if (autoplaySeconds && reducedMotion) {
          console.info('[kankatala] Stores autoplay skipped on', el, '— prefers-reduced-motion is enabled.');
        }

        var splide = new window.Splide(el, options);

        // ----- mobile state tabs: follow the slider, and drive it -----
        var tabsEl = section ? section.querySelector('[data-kk-stores-tabs]') : null;
        var tabs = tabsEl ? Array.prototype.slice.call(tabsEl.querySelectorAll('.kankatala-stores__tab')) : [];
        var stateOfIndex = realSlides.map(function (slide) { return slide.getAttribute('data-state-index'); });

        function setActiveTab(index) {
          if (!tabs.length) return;
          var state = stateOfIndex[index];
          var activeTab = null;
          tabs.forEach(function (tab) {
            var isActive = tab.getAttribute('data-state-index') === state;
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            if (isActive) activeTab = tab;
          });
          // Keep the active tab in view by scrolling the TAB ROW sideways only
          // (scrollIntoView could scroll the whole page up/down as well).
          if (activeTab && tabsEl.scrollWidth > tabsEl.clientWidth) {
            var left = activeTab.offsetLeft - (tabsEl.clientWidth - activeTab.offsetWidth) / 2;
            if (typeof tabsEl.scrollTo === 'function') tabsEl.scrollTo({ left: left, behavior: 'smooth' });
            else tabsEl.scrollLeft = left;
          }
        }

        tabs.forEach(function (tab) {
          tab.addEventListener('click', function () {
            var target = parseInt(tab.getAttribute('data-first-index'), 10);
            if (!isNaN(target)) splide.go(target);
          });
        });

        // ----- desktop position label: "1–4 of 12 stores" -----
        var rangeEl = section ? section.querySelector('[data-kk-stores-range]') : null;
        var rangeTemplate = el.getAttribute('data-range-template') || '';

        function updateRange(index) {
          if (!rangeEl) return;
          var perPage = splide.options.perPage || 1;
          if (!rangeTemplate || slideCount <= perPage) { rangeEl.hidden = true; return; }
          var start = (typeof index === 'number' ? index : splide.index) + 1;
          // The slider moves one store at a time and can loop, so a view near
          // the end wraps round to the start (12, 1, 2, 3 -> "12–3 of 12").
          // Without looping the view never runs past the last store, so the
          // modulo changes nothing there.
          var end = ((start + perPage - 2) % slideCount) + 1;
          rangeEl.textContent = rangeTemplate.replace('{start}', start).replace('{end}', end).replace('{total}', slideCount);
          rangeEl.hidden = false;
        }

        // When everything already fits in one view there is nothing to page
        // through, so CSS hides the arrows (see --static).
        function updateStatic() {
          el.classList.toggle('kankatala-stores__splide--static', slideCount <= (splide.options.perPage || 1));
        }

        // Loop clones are copies of real slides, including the attribute the
        // Theme Editor uses to find a block — strip it so the editor only ever
        // sees the real one.
        function stripCloneEditorAttrs() {
          Array.prototype.forEach.call(el.querySelectorAll('.splide__slide--clone'), function (clone) {
            clone.removeAttribute('data-shopify-editor-block');
          });
        }

        splide.on('mounted refresh', function () {
          updateStatic();
          updateRange();
          stripCloneEditorAttrs();
          setActiveTab(splide.index);
        });
        splide.on('move', function (newIndex) {
          setActiveTab(newIndex);
          updateRange(newIndex);
        });

        splide.mount();

        // Theme Editor: selecting a State block jumps the slider to that
        // state's first store.
        if (section) {
          section.addEventListener('shopify:block:select', function (event) {
            var target = event.target && event.target.closest ? event.target.closest('[data-flat-index]') : null;
            var index = target ? parseInt(target.getAttribute('data-flat-index'), 10) : NaN;
            if (!isNaN(index)) splide.go(index);
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Kankatala Luxury Grid — click-to-expand fan of panels.
     Click/tap always toggles: clicking a collapsed panel expands it (and
     collapses whichever was expanded before); clicking the panel that's
     ALREADY expanded lets its link navigate normally. Same behavior on
     desktop and mobile — no separate hover state.
  ------------------------------------------------------------------------ */
  function initKankatalaLuxuryGrid() {
    document.querySelectorAll('.kankatala-luxury-grid__grid').forEach(function (grid) {
      if (grid.dataset.kankatalaLuxuryInit) return;
      grid.dataset.kankatalaLuxuryInit = 'true';

      var panels = Array.prototype.slice.call(grid.querySelectorAll('.kankatala-luxury-grid__panel'));
      if (panels.length === 0) return;

      // "Expand trigger" setting: Click always works (so touch/keyboard
      // users on a "Hover" section aren't locked out of expanding a
      // panel) — Hover additionally expands on mouseenter, for desktop
      // pointer users who'd expect that without needing to click.
      var hoverEnabled = grid.getAttribute('data-kk-expand-trigger') === 'hover';

      function expand(panel) {
        panels.forEach(function (p) {
          p.setAttribute('aria-current', p === panel ? 'true' : 'false');
        });
      }

      panels.forEach(function (panel) {
        panel.addEventListener('click', function (event) {
          var isExpanded = panel.getAttribute('aria-current') === 'true';
          var isLink = panel.tagName === 'A';

          if (isExpanded) {
            // Already expanded — let a real link navigate normally. A
            // non-link expanded panel just has nothing further to do.
            return;
          }

          if (isLink) event.preventDefault();
          expand(panel);
        });

        if (hoverEnabled) {
          panel.addEventListener('mouseenter', function () {
            expand(panel);
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Kankatala Occasion Grid — Splide.js slider.
     Actively self-loads splide.min.js/css via real DOM createElement calls
     if window.Splide isn't already defined, rather than passively waiting
     for the section's own <script>/<link> tags to have run. This matters
     because the Shopify Theme Editor re-renders a section's HTML via AJAX
     every time a setting changes live in the customizer — and browsers
     never execute a <script> tag that arrives that way (only ones parsed
     as part of the initial page load do), so the liquid-rendered tag can
     silently never run. Without this fallback, that leaves the whole
     slider permanently invisible (splide.min.css keeps
     .splide{visibility:hidden} until Splide's own JS adds .is-initialized)
     until a full page refresh, since window.Splide never becomes defined.
  ------------------------------------------------------------------------ */
  var kkSplideLoaderPromise = null;

  function loadKkSplideAssets(jsUrl, cssUrl) {
    if (kkSplideLoaderPromise) return kkSplideLoaderPromise;

    kkSplideLoaderPromise = new Promise(function (resolve) {
      if (typeof window.Splide !== 'undefined') {
        resolve();
        return;
      }

      // <link> tags do load correctly even when injected via the Theme
      // Editor's AJAX section re-render (only <script> execution is
      // blocked that way) — this is just a defensive fallback in case the
      // liquid-rendered stylesheet tag is ever missing for some other
      // reason, not the actual fix for the bug above.
      if (cssUrl && !document.querySelector('link[data-kk-splide-css]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        link.dataset.kkSplideCss = 'true';
        document.head.appendChild(link);
      }

      if (!jsUrl) {
        console.warn('[kankatala] No splide.min.js URL available to self-load — data-splide-js-src was missing on the section\'s container.');
        resolve();
        return;
      }

      var existingScript = document.querySelector('script[data-kk-splide-js]');
      if (existingScript) {
        existingScript.addEventListener('load', function () { resolve(); });
        existingScript.addEventListener('error', function () { resolve(); });
        return;
      }

      var script = document.createElement('script');
      script.src = jsUrl;
      script.dataset.kkSplideJs = 'true';
      script.addEventListener('load', function () { resolve(); });
      script.addEventListener('error', function () {
        console.warn('[kankatala] Failed to load splide.min.js from', jsUrl, '— check the file exists in assets/ and isn\'t 0 bytes.');
        resolve();
      });
      document.head.appendChild(script);
    });

    return kkSplideLoaderPromise;
  }

  function initKankatalaOccasionSplide() {
    var containers = document.querySelectorAll('[data-kk-occasion-splide]');
    if (containers.length === 0) return;

    var jsUrl = containers[0].getAttribute('data-splide-js-src');
    var cssUrl = containers[0].getAttribute('data-splide-css-href');

    loadKkSplideAssets(jsUrl, cssUrl).then(function () {
      if (typeof window.Splide === 'undefined') {
        console.warn('[kankatala] Splide library still not available after attempting to load it — check that splide.min.js is actually in assets/ and not 0 bytes.');
        return;
      }

      initKankatalaOccasionSplideInstances(containers);
    });
  }

  function initKankatalaOccasionSplideInstances(containers) {
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    containers.forEach(function (el) {
      if (el.dataset.kkSplideInit) return;
      el.dataset.kkSplideInit = 'true';

      var slidesMobile = parseInt(el.getAttribute('data-slides-mobile'), 10) || 2;
      var slidesDesktop = parseInt(el.getAttribute('data-slides-desktop'), 10) || 5;
      var peekMobile = parseFloat(el.getAttribute('data-peek-mobile')) || 0;
      var autoplaySecondsMobile = el.getAttribute('data-autoplay-mobile');
      var autoplaySecondsDesktop = el.getAttribute('data-autoplay-desktop');
      var slideCount = el.querySelectorAll('.splide__slide').length;

      // Independent per-breakpoint slider toggle — see the matching
      // enable_slider_mobile/enable_slider_desktop settings in
      // kankatala-occasion-grid.liquid. Splide's own `destroy` breakpoint
      // option fully unmounts the slider for that breakpoint (removing all
      // the DOM changes it made) and re-mounts it automatically the moment
      // the breakpoint no longer matches — no manual CSS override needed
      // to fake a "disabled" state the way Swiper required.
      var sliderEnabledMobile = el.getAttribute('data-slider-mobile') !== 'false';
      var sliderEnabledDesktop = el.getAttribute('data-slider-desktop') !== 'false';

      // Loop ("Loop the slider" setting, on by default). type: 'loop' breaks
      // (or degrades unpredictably) once perPage reaches or exceeds the actual
      // number of slides — there's nothing left to loop to — so each
      // breakpoint only loops when there are MORE slides than fit in one view
      // THERE. This is decided per breakpoint (mobile / desktop) because the
      // two show different numbers of items: deciding once from the larger
      // count meant e.g. 5 items with 5 per row on desktop never looped on
      // mobile, where only 1 shows. Splide switches `type` at the breakpoint
      // and builds/removes the clones itself, including on resize.
      // When looping is requested but a breakpoint can't (too few slides) it
      // falls back to plain 'slide' with rewind (jumps back to the start
      // instead of just stopping at the end). With the setting OFF there is no
      // rewind either: it simply stops at the last item.
      var loopRequested = el.getAttribute('data-loop') !== 'false';
      var canLoopDesktop = loopRequested && slideCount > slidesDesktop;
      var canLoopMobile = loopRequested && slideCount > slidesMobile;
      if (loopRequested && (!canLoopDesktop || !canLoopMobile)) {
        console.info('[kankatala] Splide loop is on for', el, 'but', slideCount, 'slide(s) isn\'t more than fits in one view on', (!canLoopMobile ? 'mobile (' + slidesMobile + ' per row)' : '') + (!canLoopMobile && !canLoopDesktop ? ' or ' : '') + (!canLoopDesktop ? 'desktop (' + slidesDesktop + ' per row)' : '') + ' — that screen size scrolls to the end (then rewinds) instead of looping. Add more items, or lower "items per row", to loop there.');
      }

      // Own arrow buttons are supplied in the Liquid markup (with the
      // splide__arrow--prev/--next classes Splide looks for) only when at
      // least one of show_arrows_mobile/show_arrows_desktop is on. If
      // arrows: true is passed with NO matching buttons in the DOM, Splide
      // silently creates its own default-styled fallback pair instead of
      // just showing none — so this has to check the DOM directly rather
      // than hardcoding arrows: true, or turning every arrow setting off
      // would still leave Splide's own unstyled arrows showing.
      var hasCustomArrows = !!(el.querySelector('.splide__arrow--prev') && el.querySelector('.splide__arrow--next'));

      // Mobile "peek": whatever "items per row" is (1, 2 or 3), reserve room
      // on the right of the track so a fraction f of the NEXT item shows
      // beside the N full ones (33% = one third of it) — a visual cue that
      // the row scrolls.
      // With N full slides of width s, gap g, track width C and right padding
      // p: the slides fill C - p = N*s + (N-1)*g, and the next slide starts a
      // gap after that, so its visible part is p - g. Making that visible part
      // a fraction f of s and solving for p gives
      //     p = (f*C + g*(N*(1-f) + f)) / (N + f)
      // i.e. calc((100*f/(N+f))% + (g*(N*(1-f)+f)/(N+f))rem), g being the 1rem
      // mobile gap set below. (For N = 1 this is the original (f*C+g)/(1+f).)
      // Only applies to the mobile breakpoint, and only when there IS a next
      // item to peek at (more slides than fit) — otherwise it would just leave
      // an empty strip on the right.
      var mobilePeek = null;
      if (peekMobile > 0 && slideCount > slidesMobile) {
        var f = Math.min(peekMobile, 60) / 100;
        var n = slidesMobile;
        var peekPct = (100 * f / (n + f)).toFixed(3);
        var peekGapRem = ((n * (1 - f) + f) / (n + f)).toFixed(3);
        mobilePeek = { right: 'calc(' + peekPct + '% + ' + peekGapRem + 'rem)' };
      }

      var options = {
        type: canLoopDesktop ? 'loop' : 'slide',
        rewind: loopRequested && !canLoopDesktop,
        perPage: slidesDesktop,
        gap: '1.5rem',
        arrows: hasCustomArrows,
        pagination: false,
        destroy: !sliderEnabledDesktop,
        // Autoplay is set independently per breakpoint here too (not just
        // a single shared on/off) — same merge mechanism as perPage/gap/
        // destroy above: the values below apply on desktop, and the ones
        // inside breakpoints[699] override them below 700px.
        autoplay: !!(autoplaySecondsDesktop && !reducedMotion),
        interval: autoplaySecondsDesktop ? parseFloat(autoplaySecondsDesktop) * 1000 : undefined,
        pauseOnHover: true,
        pauseOnFocus: true,
        breakpoints: {
          699: {
            type: canLoopMobile ? 'loop' : 'slide',
            rewind: loopRequested && !canLoopMobile,
            perPage: slidesMobile,
            gap: '1rem',
            destroy: !sliderEnabledMobile,
            autoplay: !!(autoplaySecondsMobile && !reducedMotion),
            interval: autoplaySecondsMobile ? parseFloat(autoplaySecondsMobile) * 1000 : undefined
          }
        }
      };

      if (mobilePeek) options.breakpoints[699].padding = mobilePeek;

      if ((autoplaySecondsMobile || autoplaySecondsDesktop) && reducedMotion) {
        console.info('[kankatala] Splide autoplay skipped on', el, '— prefers-reduced-motion is enabled.');
      }

      console.info('[kankatala] Initializing Splide on', el, 'with', slideCount, 'slide(s), loop — mobile:', canLoopMobile, ', desktop:', canLoopDesktop, '.');
      var splideInstance = new window.Splide(el, options);

      // Autoplay keeps advancing slides on a timer while the pointer stays
      // still on screen — a new image slides under the stationary cursor
      // and gets its own fresh :hover, re-triggering the zoom transition
      // over and over. Looks like unbounded growth even though no single
      // image ever exceeds its target scale. Gate the CSS hover-zoom off
      // entirely while autoplay is genuinely running (pauseOnHover already
      // pauses it on real hover, so this class should normally only be
      // present while nothing is being hovered).
      if ((autoplaySecondsMobile || autoplaySecondsDesktop) && !reducedMotion) {
        splideInstance.on('autoplay:play', function () { el.classList.add('is-autoplay-running'); });
        splideInstance.on('autoplay:pause', function () { el.classList.remove('is-autoplay-running'); });
      }

      splideInstance.mount();
    });
  }

  /* ------------------------------------------------------------------------
     Kankatala Custom Grid — deferred (data-src/data-srcset) images.
     Replaces native loading="lazy" with a single shared IntersectionObserver
     so every grid image loads on the same, explicit trigger (a small
     pre-load margin below the viewport) instead of each browser's own
     built-in heuristic. The first block's image is rendered eager (real
     src, no data-src) directly in the Liquid, so it's never picked up here.
  ------------------------------------------------------------------------ */
  function initKankatalaCustomGridLazyImages() {
    var pictures = document.querySelectorAll('.kankatala-custom-grid__picture');
    var lazyPictures = Array.prototype.filter.call(pictures, function (picture) {
      return picture.querySelector('[data-src], [data-srcset]');
    });
    if (lazyPictures.length === 0) return;

    function hydrate(picture) {
      picture.querySelectorAll('[data-src], [data-srcset]').forEach(function (node) {
        if (node.dataset.srcset) {
          node.srcset = node.dataset.srcset;
          node.removeAttribute('data-srcset');
        }
        if (node.dataset.src) {
          node.src = node.dataset.src;
          node.removeAttribute('data-src');
        }
      });
    }

    if (typeof window.IntersectionObserver !== 'function') {
      // No IntersectionObserver support — load everything immediately
      // rather than leaving images permanently blank.
      lazyPictures.forEach(hydrate);
      return;
    }

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        hydrate(entry.target);
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '200px 0px', threshold: 0.01 });

    lazyPictures.forEach(function (picture) {
      if (picture.dataset.kkGridLazyObserved) return;
      picture.dataset.kkGridLazyObserved = 'true';
      observer.observe(picture);
    });
  }

  /* ------------------------------------------------------------------------
     Kankatala Testimonials — Splide.js fade carousel.
     Each slide holds the arch photo + quote together and crossfades to the
     next. The prev/next arrows and the photo thumbnails live OUTSIDE the
     .splide root (so the desktop layout can seat the arrows either side of
     the slider) — Splide's own arrows/pagination are off, and these plain
     buttons drive it by hand via .go('<') / .go('>') / .go(index).
     Reuses the same self-loading Splide mechanism as the Occasion Grid
     (see loadKkSplideAssets above) — see the comment there for why that's
     needed instead of just waiting on the section's own <script> tag.
  ------------------------------------------------------------------------ */
  function initKankatalaTestimonials() {
    var containers = document.querySelectorAll('[data-kk-testimonials-splide]');
    if (containers.length === 0) return;

    var jsUrl = containers[0].getAttribute('data-splide-js-src');
    var cssUrl = containers[0].getAttribute('data-splide-css-href');

    loadKkSplideAssets(jsUrl, cssUrl).then(function () {
      if (typeof window.Splide === 'undefined') {
        console.warn('[kankatala] Splide library still not available after attempting to load it — check that splide.min.js is actually in assets/ and not 0 bytes.');
        return;
      }

      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      containers.forEach(function (el) {
        if (el.dataset.kkSplideInit) return;
        el.dataset.kkSplideInit = 'true';

        var autoplaySeconds = el.getAttribute('data-autoplay');
        var slideCount = el.querySelectorAll('.splide__slide').length;

        var options = {
          type: 'fade',
          rewind: true,
          arrows: false,
          pagination: false,
          drag: slideCount > 1
        };

        if (autoplaySeconds && !reducedMotion) {
          options.autoplay = true;
          options.interval = parseFloat(autoplaySeconds) * 1000;
          options.pauseOnHover = true;
          options.pauseOnFocus = true;
        } else if (autoplaySeconds && reducedMotion) {
          console.info('[kankatala] Testimonials autoplay skipped on', el, '— prefers-reduced-motion is enabled.');
        }

        var splideInstance = new window.Splide(el, options);

        // Arrows + thumbnail row live in the same section, as siblings of
        // the Splide root rather than inside it — find them by walking up
        // to the section wrapper rather than assuming exact DOM adjacency.
        var section = el.closest('.kankatala-testimonials');

        if (section) {
          var prevButton = section.querySelector('[data-kk-testimonials-prev]');
          var nextButton = section.querySelector('[data-kk-testimonials-next]');
          // rewind: true above means '>' from the last slide wraps to the
          // first (and '<' from the first wraps to the last).
          if (prevButton) prevButton.addEventListener('click', function () { splideInstance.go('<'); });
          if (nextButton) nextButton.addEventListener('click', function () { splideInstance.go('>'); });
        }

        var thumbnailRow = section ? section.querySelector('[data-kk-testimonials-thumbnails]') : null;
        var thumbnails = thumbnailRow ? Array.prototype.slice.call(thumbnailRow.querySelectorAll('.kankatala-testimonials__thumb')) : [];

        if (thumbnails.length) {
          thumbnails.forEach(function (button, index) {
            button.addEventListener('click', function () {
              splideInstance.go(index);
            });
          });

          // 'move' (not 'moved') so the highlighted thumbnail updates the
          // instant a transition starts, in step with the crossfade,
          // rather than after it finishes.
          splideInstance.on('move', function (newIndex) {
            thumbnails.forEach(function (button, index) {
              button.setAttribute('aria-current', index === newIndex ? 'true' : 'false');
            });
          });
        }

        splideInstance.mount();
      });
    });
  }

  /* ------------------------------------------------------------------------
     Kankatala Specific Information — PDP.
     This block can render through two different code paths on this theme
     (main-product.liquid's own "content below gallery" loop, AND
     product-info.liquid's own internal block loop), each with a
     completely different wrapper markup (.kankatala-specific-info-wrapper
     from our own liquid vs. .product-info__block-item[data-block-id]
     from product-info.liquid, which we don't control). A Liquid-only
     wrapper class can only ever reach ONE of those two — this finds the
     right accordion instance purely by its visible title text instead,
     so it works no matter which markup rendered it, and adds one class
     directly onto its .accordion__content for kankatala-custom.css to
     target. Nothing about the block's own content/metafield chips is
     touched, only a class is added to its wrapper.
  ------------------------------------------------------------------------ */
  function initKankatalaSpecificInformation() {
    document.querySelectorAll('.accordion__content').forEach(function (content) {
      if (content.dataset.kkSpecificInfoChecked) return;
      content.dataset.kkSpecificInfoChecked = 'true';

      var accordion = content.closest('accordion-disclosure, .accordion');
      if (!accordion) return;

      var titleEl = accordion.querySelector('.accordion__toggle > span:first-child');
      if (!titleEl) return;

      var titleText = titleEl.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      if (titleText !== 'specific information') return;

      content.classList.add('kankatala-specific-info-content');

      // Restructure the flat label/value <li> list (label, value, label,
      // value, ...) into bordered tiles — one tile per field, label
      // stacked above its value. This can't be done with CSS alone since
      // label and value are separate sibling <li> elements; grid-auto-flow
      // tricks can stack pairs into a column but can't also wrap into
      // rows of a fixed tile count the way flex/grid on real wrapper
      // elements can. This only re-wraps text that Shopify has ALREADY
      // resolved server-side (including any connected metafield/Dynamic
      // Source values) — nothing about the underlying content or its
      // metafield connections is touched, only how the already-rendered
      // text is grouped for display.
      var list = content.querySelector(':scope > ul, :scope > ol') || content.querySelector('ul, ol');
      if (!list) return;

      var items = Array.prototype.slice.call(list.children).filter(function (el) {
        return el.tagName === 'LI';
      });

      var grid = document.createElement('div');
      grid.className = 'kankatala-specific-info-grid';

      function isKkValueMissing(text) {
        var trimmed = text.replace(/\s+/g, ' ').trim().toLowerCase();
        return trimmed === '' || trimmed === 'null';
      }

      for (var i = 0; i + 1 < items.length; i += 2) {
        var labelLi = items[i];
        var valueLi = items[i + 1];

        // Skip this field entirely (don't render a tile at all) if its
        // value is blank or literally "null" — e.g. Fabric has no value
        // set for this product. Other fields that DO have a value still
        // show normally.
        if (isKkValueMissing(valueLi.textContent)) continue;

        var field = document.createElement('div');
        field.className = 'kankatala-specific-info-field';

        var labelEl = document.createElement('div');
        labelEl.className = 'kankatala-specific-info-field__label';
        // Labels are plain text (no metafield chip needed on the label
        // side), so textContent + stripping a trailing colon is safe —
        // values use innerHTML below to preserve their resolved content.
        labelEl.textContent = labelLi.textContent.replace(/:\s*$/, '').trim();

        var valueEl = document.createElement('div');
        valueEl.className = 'kankatala-specific-info-field__value';
        valueEl.innerHTML = valueLi.innerHTML;

        field.appendChild(labelEl);
        field.appendChild(valueEl);
        grid.appendChild(field);
      }

      // Odd leftover (unpaired) item, if the list has an odd count —
      // shown as a value-only tile rather than silently dropped, unless
      // it's also blank/null.
      if (items.length % 2 !== 0) {
        var leftover = items[items.length - 1];
        if (!isKkValueMissing(leftover.textContent)) {
          var field = document.createElement('div');
          field.className = 'kankatala-specific-info-field';
          var valueEl = document.createElement('div');
          valueEl.className = 'kankatala-specific-info-field__value';
          valueEl.innerHTML = leftover.innerHTML;
          field.appendChild(valueEl);
          grid.appendChild(field);
        }
      }

      // If every single field turned out to be missing/null (e.g. Fabric,
      // Material, and everything else are all unset for this product),
      // hide the whole "Specific Information" block entirely — including
      // its title/toggle — rather than showing an empty accordion with
      // nothing inside it.
      if (grid.children.length === 0) {
        accordion.style.display = 'none';
        return;
      }

      list.replaceWith(grid);
    });
  }

  /* ------------------------------------------------------------------------
     Kankatala Collections — Splide carousel, one per tab panel.
     Reuses the same self-loading Splide mechanism as Occasion Grid and
     Testimonials (loadKkSplideAssets above) — see the comment there for
     why that's needed instead of just waiting on a <script> tag. Loop,
     arrows, and autoplay are all native Splide options here, read from
     data attributes set in kankatala-collections.liquid, rather than
     custom-built behavior.

     x-tabs hides inactive tab panels (display: none), and Splide sizes
     itself incorrectly if mounted while its container has zero width —
     so every instance is still mounted up front (off-screen panels still
     get a correctly-configured instance), and clicking any tab button
     triggers a .refresh() on every mounted instance shortly after, which
     corrects the layout once that panel becomes visible.
  ------------------------------------------------------------------------ */
  function initKankatalaCollectionsSplide() {
    var containers = document.querySelectorAll('[data-kk-collections-splide]');
    if (containers.length === 0) return;

    var jsUrl = containers[0].getAttribute('data-splide-js-src');
    var cssUrl = containers[0].getAttribute('data-splide-css-href');

    loadKkSplideAssets(jsUrl, cssUrl).then(function () {
      if (typeof window.Splide === 'undefined') {
        console.warn('[kankatala] Splide library still not available after attempting to load it — check that splide.min.js is actually in assets/ and not 0 bytes.');
        return;
      }

      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var instances = [];

      containers.forEach(function (el) {
        if (el.dataset.kkSplideInit) return;
        el.dataset.kkSplideInit = 'true';

        var perPageDesktop = parseInt(el.getAttribute('data-per-page-desktop'), 10) || 4;
        var perPageMobile = parseInt(el.getAttribute('data-per-page-mobile'), 10) || 2;
        var loopRequested = el.getAttribute('data-loop') === 'true';
        var showArrows = el.getAttribute('data-arrows') === 'true';
        var autoplaySeconds = el.getAttribute('data-autoplay-seconds');
        var slideCount = el.querySelectorAll('.splide__slide').length;

        // type: 'loop' breaks once perPage reaches or exceeds the actual
        // number of slides — same caveat as Occasion Grid. Fall back to
        // plain 'slide' with rewind when there isn't genuinely more
        // content than fits, regardless of what the setting requests.
        var canLoop = loopRequested && slideCount > Math.max(perPageDesktop, perPageMobile);
        if (loopRequested && !canLoop) {
          console.info('[kankatala] Collections Splide loop requested on', el, 'but', slideCount, 'slide(s) isn\'t more than the largest "products per row" setting (desktop', perPageDesktop, ', mobile', perPageMobile, ') — falling back to non-looping.');
        }

        // Splide lays itself out independently of the theme's own
        // --product-list-horizontal-spacing-factor grid CSS (that only
        // applies to the stacked/static grid mode), so the gap here is a
        // fixed, sensible default rather than settings-driven.
        var options = {
          type: canLoop ? 'loop' : 'slide',
          rewind: !canLoop,
          perPage: perPageDesktop,
          perMove: 1,
          gap: '1.5rem',
          arrows: showArrows,
          pagination: false,
          autoplay: !!(autoplaySeconds && !reducedMotion),
          interval: autoplaySeconds ? parseFloat(autoplaySeconds) * 1000 : undefined,
          pauseOnHover: true,
          pauseOnFocus: true,
          breakpoints: {
            699: {
              perPage: perPageMobile,
              gap: '1rem'
            }
          }
        };

        if (autoplaySeconds && reducedMotion) {
          console.info('[kankatala] Collections Splide autoplay skipped on', el, '— prefers-reduced-motion is enabled.');
        }

        // Mobile "peek": whatever "Products per row (mobile)" is (1, 2 or 3),
        // reserve room on the right of the track so a fraction f of the NEXT
        // slide shows beside the N full ones (33% = one third) — a cue that
        // there is more to swipe. With N slides of width s, gap g, track width
        // C and right padding p, the slides fill C - p = N*s + (N-1)*g and the
        // next slide starts a gap after that, so its visible part is p - g.
        // Making that a fraction f of s gives
        //     p = (f*C + g*(N*(1-f) + f)) / (N + f)
        // i.e. calc((100*f/(N+f))% + (g*(N*(1-f)+f)/(N+f))rem), with g being
        // the 1rem mobile gap above. Same maths as the Occasion Grid, Stores and
        // Products sliders. Mobile only (the 699px breakpoint), and only when
        // there IS a next slide to peek at.
        var peekMobile = parseFloat(el.getAttribute('data-peek-mobile')) || 0;
        if (peekMobile > 0 && slideCount > perPageMobile) {
          var f = Math.min(peekMobile, 60) / 100;
          var n = perPageMobile;
          var gapRem = 1;
          var peekPct = (100 * f / (n + f)).toFixed(3);
          var peekGapRem = (gapRem * (n * (1 - f) + f) / (n + f)).toFixed(3);
          options.breakpoints[699].padding = { right: 'calc(' + peekPct + '% + ' + peekGapRem + 'rem)' };
        }

        var splideInstance = new window.Splide(el, options);
        splideInstance.mount();
        instances.push(splideInstance);
      });

      // The FIRST tab's panel is already visible on load (x-tabs shows it
      // immediately, before anyone clicks anything) — but if its Splide
      // instance mounted before the surrounding layout (x-tabs, custom
      // elements upgrading, fonts, etc.) had actually settled, it can come
      // out sized wrong or empty-looking, and previously nothing ever
      // corrected it since the tab-click refresh below only fires for
      // tabs someone actually clicks. Refresh every instance once more
      // shortly after mount to fix that first-tab case too.
      setTimeout(function () {
        instances.forEach(function (instance) {
          if (instance && typeof instance.refresh === 'function') instance.refresh();
        });
      }, 100);

      // Second safety net — fonts/images can still shift the layout after
      // the short timeout above, so refresh again once everything has
      // actually finished loading.
      window.addEventListener('load', function () {
        instances.forEach(function (instance) {
          if (instance && typeof instance.refresh === 'function') instance.refresh();
        });
      });

      // Tabs start with only the first panel visible; refresh every
      // instance once more shortly after any tab click so whichever
      // Splide was just revealed lays itself out at its real width.
      document.querySelectorAll('.kankatala-collections__tab').forEach(function (tabButton) {
        if (tabButton.dataset.kkCollectionsRefreshBound) return;
        tabButton.dataset.kkCollectionsRefreshBound = 'true';
        tabButton.addEventListener('click', function () {
          setTimeout(function () {
            instances.forEach(function (instance) {
              if (instance && typeof instance.refresh === 'function') instance.refresh();
            });
          }, 50);
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Kankatala Products (kankatala-featured-collections.liquid) — a Splide
     slider per collection, where EVERY product is its own slide and the slider
     moves one product at a time (perMove: 1).
       Desktop (700px+): "Items per row (desktop)" products in view, no peek.
       Mobile  (<700px): "Items per row (mobile)" (1, 2 or 3) products in view,
                         plus a fraction of the NEXT one peeking in (default a
                         third) so it is clear there is more to swipe.
     Options come from data attributes. Loop is decided separately for each
     breakpoint and only when there are MORE products than fit in one view
     there (type: 'loop' breaks once perPage reaches the slide count); with the
     setting on but too few products it falls back to 'slide' + rewind, and with
     it off the slider simply stops at the last product. Auto-scroll is optional
     (seconds from the section), pauses on hover/focus and is skipped for
     visitors who prefer reduced motion.
     Same self-loading Splide mechanism as the other sliders (loadKkSplideAssets).
  ------------------------------------------------------------------------ */
  function initKankatalaProducts() {
    var roots = document.querySelectorAll('[data-kk-products-splide]');
    if (roots.length === 0) return;

    var jsUrl = roots[0].getAttribute('data-splide-js-src');
    var cssUrl = roots[0].getAttribute('data-splide-css-href');

    loadKkSplideAssets(jsUrl, cssUrl).then(function () {
      if (typeof window.Splide === 'undefined') {
        console.warn('[kankatala] Splide library still not available after attempting to load it — check that splide.min.js is actually in assets/ and not 0 bytes.');
        return;
      }

      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      Array.prototype.forEach.call(roots, function (el) {
        if (el.dataset.kkProductsInit) return;
        el.dataset.kkProductsInit = 'true';

        var slideCount = el.querySelectorAll('.splide__slide').length;
        if (slideCount === 0) return;

        function num(attr, fallback) {
          var v = parseFloat(el.getAttribute(attr));
          return isNaN(v) ? fallback : v;
        }

        var perPageDesktop = Math.min(6, Math.max(1, Math.round(num('data-per-page-desktop', 4))));
        var perPageMobile = Math.min(3, Math.max(1, Math.round(num('data-per-page-mobile', 2))));
        var peekMobile = num('data-peek-mobile', 0);
        var gapMobile = num('data-gap-mobile', 0.625);   // rem
        var gapDesktop = num('data-gap-desktop', 1);     // rem
        var loopRequested = el.getAttribute('data-loop') !== 'false';
        var autoplaySeconds = num('data-autoplay-seconds', 0);
        var hasArrows = !!(el.querySelector('.splide__arrow--prev') && el.querySelector('.splide__arrow--next'));

        // One breakpoint's worth of options. Splide applies only the SINGLE
        // closest matching breakpoint on top of the base options (they don't
        // stack), so each entry carries everything that differs.
        function layout(perPage, gapRem) {
          var canLoop = loopRequested && slideCount > perPage;
          return { type: canLoop ? 'loop' : 'slide', rewind: loopRequested && !canLoop, perPage: perPage, gap: gapRem + 'rem' };
        }

        var base = layout(perPageDesktop, gapDesktop);
        var mobile = layout(perPageMobile, gapMobile);

        // Mobile "peek": reserve room on the right of the track so a fraction f
        // of the NEXT product shows beside the N full ones. With N slides of
        // width s, gap g, track width C and right padding p, the slides fill
        // C - p = N*s + (N-1)*g and the next slide starts a gap after that, so
        // its visible part is p - g. Making that a fraction f of s gives
        //     p = (f*C + g*(N*(1-f) + f)) / (N + f)
        // i.e. calc((100*f/(N+f))% + (g*(N*(1-f)+f)/(N+f))rem). Only when there
        // IS a next product to peek at, and never on desktop.
        if (peekMobile > 0 && slideCount > perPageMobile) {
          var f = Math.min(peekMobile, 60) / 100;
          var n = perPageMobile;
          var peekPct = (100 * f / (n + f)).toFixed(3);
          var peekGapRem = (gapMobile * (n * (1 - f) + f) / (n + f)).toFixed(3);
          mobile.padding = { right: 'calc(' + peekPct + '% + ' + peekGapRem + 'rem)' };
        }

        var options = {
          type: base.type,
          rewind: base.rewind,
          perPage: base.perPage,
          gap: base.gap,
          perMove: 1,                      // one product per arrow click / swipe / auto-scroll step
          arrows: hasArrows,
          pagination: false,
          autoplay: !!(autoplaySeconds && !reducedMotion),
          interval: autoplaySeconds ? autoplaySeconds * 1000 : undefined,
          pauseOnHover: true,
          pauseOnFocus: true,
          breakpoints: { 699: mobile }
        };

        if (loopRequested && (slideCount <= perPageDesktop || slideCount <= perPageMobile)) {
          console.info('[kankatala] Products slider: loop is on but', slideCount, 'product(s) isn\'t more than fits in one view at some screen sizes (desktop', perPageDesktop, '/ mobile', perPageMobile, ') — those sizes scroll to the end instead of looping.');
        }

        var splide = new window.Splide(el, options);

        // Everything already fits in one view: nothing to page through, so CSS hides the arrows.
        function updateStatic() {
          el.classList.toggle('kankatala-products__splide--static', slideCount <= (splide.options.perPage || 1));
        }
        splide.on('mounted refresh', updateStatic);
        splide.mount();

        // A slider inside a collection tab that isn't showing has no width when
        // it mounts, so its slides are mis-sized. When it first gets a real width
        // (the tab is opened, or the section becomes visible) rebuild it.
        if (typeof window.ResizeObserver === 'function') {
          var lastWidth = el.offsetWidth;
          new window.ResizeObserver(function () {
            var w = el.offsetWidth;
            if (w > 0 && lastWidth === 0) splide.refresh();
            lastWidth = w;
          }).observe(el);
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Kankatala About Journey (kankatala-about-journey.liquid) — a Splide slider
     of chapter cards: every card is one slide and the slider moves ONE card at
     a time (perMove: 1).
       Desktop (700px+): "Items per row (desktop)" cards in view.
       Mobile  (<700px): "Items per row (mobile)" (1, 2 or 3) cards in view.
     On BOTH, a fraction of the NEXT card (default a third) peeks in beside the
     full ones, so it is clear there is more. Loop is decided separately for each
     breakpoint and only when there are MORE cards than fit in one view there
     (type: 'loop' breaks once perPage reaches the slide count); with the setting
     on but too few cards it falls back to 'slide' + rewind, and with it off the
     slider stops at the last card. Arrows are shown per device by CSS; this
     only needs them in the markup. Options come from data attributes. Same
     self-loading Splide mechanism as the other sliders (loadKkSplideAssets).
  ------------------------------------------------------------------------ */
  function initKankatalaJourney() {
    var roots = document.querySelectorAll('[data-kk-journey-splide]');
    if (roots.length === 0) return;

    var jsUrl = roots[0].getAttribute('data-splide-js-src');
    var cssUrl = roots[0].getAttribute('data-splide-css-href');

    loadKkSplideAssets(jsUrl, cssUrl).then(function () {
      if (typeof window.Splide === 'undefined') {
        console.warn('[kankatala] Splide library still not available after attempting to load it — check that splide.min.js is actually in assets/ and not 0 bytes.');
        return;
      }

      Array.prototype.forEach.call(roots, function (el) {
        if (el.dataset.kkJourneyInit) return;
        el.dataset.kkJourneyInit = 'true';

        var slideCount = el.querySelectorAll('.splide__slide').length;
        if (slideCount === 0) return;

        function num(attr, fallback) {
          var v = parseFloat(el.getAttribute(attr));
          return isNaN(v) ? fallback : v;
        }

        var perPageDesktop = Math.min(5, Math.max(1, Math.round(num('data-per-page-desktop', 3))));
        var perPageMobile = Math.min(3, Math.max(1, Math.round(num('data-per-page-mobile', 1))));
        var peek = Math.min(60, Math.max(0, num('data-peek', 0))) / 100;
        var gapMobile = num('data-gap-mobile', 0.875);   // rem
        var gapDesktop = num('data-gap-desktop', 1.25);  // rem
        var loopRequested = el.getAttribute('data-loop') !== 'false';
        var hasArrows = !!(el.querySelector('.splide__arrow--prev') && el.querySelector('.splide__arrow--next'));

        // Room on the right of the track so a fraction f of the NEXT slide shows
        // beside the N full ones. With N slides of width s, gap g, track width C
        // and right padding p, the slides fill C - p = N*s + (N-1)*g and the next
        // slide starts a gap after that, so its visible part is p - g. Making
        // that a fraction f of s gives  p = (f*C + g*(N*(1-f) + f)) / (N + f),
        // i.e. calc((100*f/(N+f))% + (g*(N*(1-f)+f)/(N+f))rem). Only when there
        // IS a next card to peek at.
        function peekPadding(n, gapRem) {
          if (!(peek > 0) || slideCount <= n) return 0;
          var pct = (100 * peek / (n + peek)).toFixed(3);
          var rem = (gapRem * (n * (1 - peek) + peek) / (n + peek)).toFixed(3);
          return { right: 'calc(' + pct + '% + ' + rem + 'rem)' };
        }

        // One breakpoint's worth of options. Splide applies only the SINGLE
        // closest matching breakpoint on top of the base options (they don't
        // stack), so each entry carries everything that differs.
        function layout(perPage, gapRem) {
          var canLoop = loopRequested && slideCount > perPage;
          return { type: canLoop ? 'loop' : 'slide', rewind: loopRequested && !canLoop, perPage: perPage, gap: gapRem + 'rem', padding: peekPadding(perPage, gapRem) };
        }

        var base = layout(perPageDesktop, gapDesktop);

        var splide = new window.Splide(el, {
          type: base.type,
          rewind: base.rewind,
          perPage: base.perPage,
          gap: base.gap,
          padding: base.padding,
          perMove: 1,                      // one card per arrow click / swipe
          arrows: hasArrows,
          pagination: false,
          breakpoints: { 699: layout(perPageMobile, gapMobile) }
        });

        // Everything already fits in one view: nothing to page through, so CSS hides the arrows.
        function updateStatic() {
          el.classList.toggle('ks-journey__splide--static', slideCount <= (splide.options.perPage || 1));
        }
        splide.on('mounted refresh', updateStatic);

        // Splide centres its arrows on the whole slider; here they read better
        // centred on the PHOTO part of a card, so measure that and hand it to CSS.
        function placeArrows() {
          var media = el.querySelector('.ks-journey__media');
          if (media) el.style.setProperty('--ks-journey-arrow-top', Math.round(media.offsetHeight / 2) + 'px');
        }
        splide.on('mounted refresh resized', placeArrows);
        splide.mount();
      });
    });
  }

  /* ------------------------------------------------------------------------
     Collection filters, mobile drawer: reload the product grid in the
     background on every change. Prestige only does this for the sidebar;
     the drawer waits for close because Prestige's facet:update swaps the whole
     section (drawer included) and would close it. Here only the results
     (grid/pagination or empty state), the result count and the drawer's own
     filter form are swapped.
     Delegated on document, so it survives section re-renders; guarded so it
     binds once.
  ------------------------------------------------------------------------ */
  function initKankatalaDrawerFacets() {
    if (window.kankatalaDrawerFacetsInit) return;
    window.kankatalaDrawerFacetsInit = true;

    var controller = null;

    document.addEventListener('change', function (event) {
      var facets = event.target.closest && event.target.closest('#facets-drawer facets-form');
      if (!facets) return;

      var form = facets.querySelector('form');
      var sectionId = facets.getAttribute('section-id');
      var section = document.getElementById('shopify-section-' + sectionId);
      if (!form || !section) return;

      var url = new URL(form.action);
      new URLSearchParams(new FormData(form)).forEach(function (value, key) {
        url.searchParams.append(key, value);
      });
      ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach(function (key) {
        if (url.searchParams.get(key) === '') url.searchParams.delete(key);
      });
      var pageUrl = new URL(url);
      url.searchParams.set('section_id', sectionId);

      if (controller) controller.abort();
      controller = new AbortController();

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      fetch(url.toString(), { signal: controller.signal })
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          // Swap only the results (product grid + pagination, or the "no
          // results" state). The drawer itself lives inside .collection, so
          // replacing .collection as a whole would close it.
          var resultsSelector = '.collection > .collection__main, .collection > .empty-state';
          var newResults = doc.querySelector(resultsSelector);
          var oldResults = section.querySelector(resultsSelector);
          if (newResults && oldResults) {
            oldResults.replaceWith(document.importNode(newResults, true));
          }

          var newCount = doc.querySelector('.collection-toolbar__products-count');
          var oldCount = section.querySelector('.collection-toolbar__products-count');
          if (newCount && oldCount) oldCount.replaceWith(document.importNode(newCount, true));

          // Refresh the drawer's filters (counts / disabled values), keeping
          // open groups open. A fresh element also resets Prestige's dirty
          // flag, so closing the drawer won't trigger a second full reload.
          var newFacets = doc.querySelector('#facets-drawer facets-form');
          if (newFacets) {
            var open = Array.prototype.map.call(facets.querySelectorAll('details'), function (d) { return d.open; });
            newFacets.querySelectorAll('details').forEach(function (d, i) { d.open = !!open[i]; });
            facets.replaceWith(document.importNode(newFacets, true));
          }

          history.replaceState({}, '', pageUrl.toString());
        })
        .catch(function () { /* aborted by a newer change, or offline: ignore */ })
        .then(function () {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        });
    });
  }

  /* ------------------------------------------------------------------------
     Collection filters: "In stock" on by default, hidden from the URL.
     Shopify can't pre-apply a filter server-side, so on the first unfiltered
     visit of a session the page reloads once with filter.v.availability=1.
     The parameter is then removed from the address bar (the page stays
     filtered), and kept out of every URL Prestige / our drawer script write
     with history.replaceState. Session flags per collection:
       kankatala-instock:<path>        default already applied this session
       kankatala-instock-hidden:<path> In stock is on but hidden from the URL
     so a refresh restores the hidden In stock filter, while a shopper who
     unticks it (or clears filters) keeps seeing everything.
     Prestige's sort builds its URL from location.href, so the hidden
     parameter is put back just before Prestige reads it.
  ------------------------------------------------------------------------ */
  function initKankatalaDefaultInStock() {
    if (window.kankatalaDefaultInStockInit) return;
    var input = document.querySelector('facets-form input[name="filter.v.availability"]');
    if (!input) return;
    window.kankatalaDefaultInStockInit = true;

    var path = window.location.pathname;
    var keyDone = 'kankatala-instock:' + path;
    var keyHidden = 'kankatala-instock-hidden:' + path;

    function read(key) {
      try { return sessionStorage.getItem(key); } catch (e) { return null; }
    }
    function write(key, value) {
      try {
        if (value === null) sessionStorage.removeItem(key);
        else sessionStorage.setItem(key, value);
      } catch (e) { /* storage blocked: the URL simply keeps the parameter */ }
    }

    var url = new URL(window.location.href);
    var hasFilter = false;
    url.searchParams.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) hasFilter = true;
    });

    // first visit of the session, or a refresh while In stock is hidden: apply it
    var needDefault = !hasFilter && !read(keyDone);
    var needRestore = read(keyHidden) === '1' && !url.searchParams.has('filter.v.availability');
    if (needDefault || needRestore) {
      write(keyDone, '1');
      write(keyHidden, '1');
      url.searchParams.set('filter.v.availability', '1');
      window.location.replace(url.toString());
      return;
    }

    var originalReplaceState = history.replaceState.bind(history);

    // returns the URL without a lone "In stock" value; records whether it was on
    function hideInStock(target) {
      var next = new URL(target, window.location.href);
      if (next.pathname !== path) return target;
      var values = next.searchParams.getAll('filter.v.availability');
      if (values.length === 1 && values[0] === '1') {
        next.searchParams.delete('filter.v.availability');
        write(keyHidden, '1');
        return next.toString();
      }
      write(keyHidden, null);
      return target;
    }

    history.replaceState = function (state, title, target) {
      if (target !== undefined && target !== null) target = hideInStock(String(target));
      return originalReplaceState(state, title, target);
    };

    // tidy the address bar for the page we just loaded
    if (url.searchParams.getAll('filter.v.availability').join() === '1') {
      history.replaceState(history.state, '', url.toString());
    }

    // sort: let Prestige read the real (filtered) URL, it is hidden again right after
    document.addEventListener('listbox:change', function (event) {
      if (!event.target.closest || !event.target.closest('facets-sort-popover')) return;
      if (read(keyHidden) !== '1') return;
      var real = new URL(window.location.href);
      real.searchParams.set('filter.v.availability', '1');
      originalReplaceState(history.state, '', real.toString());
    }, true);
  }

  /* ------------------------------------------------------------------------
     PDP tailoring options (snippets/kankatala-tailoring-options.liquid).
     Ticked add-ons are separate cart lines (own price and stock), added in ONE
     atomic /cart/add.js call together with the main product. Prestige's
     <product-form> adds a single line, so while an add-on is ticked we take
     over the submit (capture phase) and fire the same events Prestige fires
     after a successful add (variant:add, cart:change) so the cart drawer
     behaves identically. Add-ons are linked to the main line through hidden
     (underscore) properties; the main line shows a readable "Add-ons" property.
     Prestige coupling: theme.js ProductForm onSubmit (re-check on upgrade).
  ------------------------------------------------------------------------ */
  function kankatalaMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency', currency: currency, maximumFractionDigits: 0
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(0);
    }
  }

  function kankatalaTailoringChecked(root) {
    return Array.prototype.slice.call(root.querySelectorAll('.kankatala-tailoring__input:checked:not(:disabled)'));
  }

  function kankatalaTailoringRender(root) {
    // The add-on amount is shown inside the main price (assets/kankatala-pdp.js), not as a line here
    var checked = kankatalaTailoringChecked(root);
    document.documentElement.classList.toggle('kankatala-tailoring-active', checked.length > 0);
  }

  function kankatalaTailoringAdd(form, root, checked) {
    var data = new FormData(form);
    var productId = root.dataset.productId;
    var properties = {};
    data.forEach(function (value, key) {
      var match = key.match(/^properties\[(.*)\]$/);
      if (match) properties[match[1]] = value;
    });

    properties['_Add-ons'] = checked.map(function (input) {
      return input.dataset.title + ' (' + kankatalaMoney(parseInt(input.dataset.price, 10)) + ')';
    }).join(', ');
    properties['_mainProduct'] = productId;

    var mainItem = {
      id: parseInt(form.id.value, 10),
      quantity: parseInt(data.get('quantity') || '1', 10),
      properties: properties
    };
    if (data.get('selling_plan')) mainItem.selling_plan = data.get('selling_plan');

    var items = [mainItem];
    checked.forEach(function (input) {
      items.push({
        id: parseInt(input.dataset.variantId, 10),
        quantity: 1,
        properties: { '_mainProductID': productId }
      });
    });

    var sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    var buttons = Array.prototype.filter.call(form.elements, function (el) { return el.type === 'submit'; });
    buttons.forEach(function (button) { button.setAttribute('aria-busy', 'true'); });
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    var root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root_url + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: items,
        sections: sectionsToBundle.join(','),
        sections_url: root_url + 'variants/' + mainItem.id
      })
    })
      .then(function (response) {
        return response.json().then(function (json) { return { ok: response.ok, json: json }; });
      })
      .then(function (result) {
        if (!result.ok) {
          form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: result.json.description } }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }

        var themeSettings = (window.themeVariables && window.themeVariables.settings) || {};
        if (themeSettings.cartType === 'page' || themeSettings.pageType === 'cart') {
          window.location.href = root_url + 'cart';
          return;
        }

        return fetch(root_url + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          cart.sections = result.json.sections;
          form.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: result.json.items || [result.json], cart: cart, onSuccessDo: data.get('on_success') }
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', onSuccessDo: data.get('on_success'), cart: cart }
          }));
        });
      })
      .catch(function () {
        form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: 'Something went wrong. Please try again.' } }));
      })
      .then(function () {
        buttons.forEach(function (button) { button.removeAttribute('aria-busy'); });
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      });
  }

  function initKankatalaTailoring() {
    document.querySelectorAll('[data-kankatala-tailoring]').forEach(function (root) {
      if (root.dataset.kankatalaInit) return;
      root.dataset.kankatalaInit = 'true';
      root.addEventListener('change', function () { kankatalaTailoringRender(root); });
      kankatalaTailoringRender(root);
    });

    // One document-level listener for the whole page, bound once
    if (window.kankatalaTailoringSubmitInit) return;
    window.kankatalaTailoringSubmitInit = true;

    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement) || (form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;

      var section = form.closest('.shopify-section');
      var root = section && section.querySelector('[data-kankatala-tailoring]');
      if (!root) return;

      var checked = kankatalaTailoringChecked(root);
      if (!checked.length || !form.checkValidity()) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.submitter && event.submitter.getAttribute('aria-busy') === 'true') return;
      kankatalaTailoringAdd(form, root, checked);
    }, true);
  }

  /* ------------------------------------------------------------------------
     Tailoring add-on lines are separate cart lines shown INSIDE their saree's
     row (price included, chips without prices — snippets/kankatala-cart-line-item
     / kankatala-cart-addons). Keep them in step with their saree here: remove
     them when the saree is gone and match their quantity to the saree's. Runs
     after every cart:change and once on load. No-op for carts without add-ons.
     Link properties: _mainProduct / _mainProductID (this theme) or Dawn's
     mainProduct / mainProductID (carts started on the old theme).
  ------------------------------------------------------------------------ */
  function kankatalaMainOf(item) {
    var p = item.properties || {};
    return p._mainProduct || p.mainProduct;
  }

  function kankatalaParentOf(item) {
    var p = item.properties || {};
    return p._mainProductID || p.mainProductID;
  }

  /* One /cart/update.js call (e.g. a saree + all its add-ons), then the same
     cart:change Prestige fires after a line change, with the bundled sections so
     the drawer re-renders; the cart page just reloads. */
  function kankatalaCartUpdate(updates) {
    var sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    var root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root_url + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: updates, sections: sectionsToBundle.join(',') })
    })
      .then(function (response) { return response.json().then(function (cart) { return { ok: response.ok, cart: cart }; }); })
      .then(function (result) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        if (!result.ok) return;
        var themeSettings = (window.themeVariables && window.themeVariables.settings) || {};
        if (themeSettings.pageType === 'cart') {
          window.location.reload();
          return;
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'line-item:change', cart: result.cart }
        }));
      })
      .catch(function () {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      });
  }

  function kankatalaTailoringSyncCart(cart) {
    var items = (cart && cart.items) || [];
    var mainQty = {};
    items.forEach(function (item) {
      var id = kankatalaMainOf(item);
      if (id) mainQty[id] = (mainQty[id] || 0) + item.quantity;
    });

    var updates = {};
    var changed = false;
    items.forEach(function (item) {
      var id = kankatalaParentOf(item);
      if (!id) return;
      var wanted = mainQty[id] ? 1 : 0; // one of each add-on per main product
      if (item.quantity !== wanted) {
        updates[item.key] = wanted;
        changed = true;
      }
    });
    if (!changed) return;

    var root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root_url + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: updates })
    }).then(function () {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    });
  }

  function initKankatalaTailoringCartSync() {
    if (window.kankatalaTailoringCartSyncInit) return;
    window.kankatalaTailoringCartSyncInit = true;

    document.addEventListener('cart:change', function (event) {
      kankatalaTailoringSyncCart(event.detail && event.detail.cart);
    });

    // Saree bin: the saree and all its add-on lines in ONE update (the keys are
    // rendered on the link). Capture phase, so Prestige's <line-item-quantity>
    // (which would remove the saree alone) never sees the click.
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('[data-kankatala-remove-line]');
      if (!link) return;
      event.preventDefault();
      event.stopPropagation();
      if (link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');

      var updates = {};
      link.getAttribute('data-kankatala-remove-line').split(',').forEach(function (key) {
        if (key) updates[key] = 0;
      });
      kankatalaCartUpdate(updates);
    }, true);

    // × on an add-on chip: that add-on line only; the saree's price drops by it
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('[data-kankatala-remove-addon]');
      if (!link) return;
      event.preventDefault();

      var updates = {};
      updates[new URL(link.href, window.location.href).searchParams.get('id')] = 0;
      kankatalaCartUpdate(updates);
    });

    var root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root_url + 'cart.js').then(function (r) { return r.json(); }).then(kankatalaTailoringSyncCart).catch(function () {});
  }

  // ---------------------------------------------------------------------------
  // Cart: add-on chips (snippets/kankatala-cart-addons.liquid)
  //
  // Beyond 4 add-ons, Liquid renders every chip but marks the ones past the
  // third as --extra, which CSS hides; the "+n more" chip sets .is-expanded on
  // the chip group (and hides itself). Delegated from document so it keeps
  // working after the cart drawer replaces its own contents on cart:change.
  // ---------------------------------------------------------------------------
  function initKankatalaCartAddons() {
    if (window.kankatalaCartAddonsInit) return;
    window.kankatalaCartAddonsInit = true;

    document.addEventListener('click', function (event) {
      var toggle = event.target.closest && event.target.closest('[data-kankatala-addon-toggle]');
      if (!toggle) return;
      event.preventDefault();

      var group = toggle.closest('.kankatala-cart-addons-chips');
      if (!group) return;
      group.classList.add('is-expanded');
      toggle.setAttribute('aria-expanded', 'true');
      var first = group.querySelector('.kankatala-cart-addon-chip--extra .kankatala-cart-addon-chip__remove');
      if (first) first.focus();
    });
  }

  // ---------------------------------------------------------------------------
  // Cart: international free-shipping progress bar
  // (snippets/kankatala-cart-shipping.liquid)
  //
  // Mirrors the contract of Prestige's own <free-shipping-bar> (theme.js) --
  // recompute from the cart:change payload, shippable lines only, less
  // cart-level discounts -- but drives a progress track as well as the
  // message, which Prestige's element cannot do. Liquid pre-renders the
  // correct state, so this only has to keep it in step after a cart mutation.
  // ---------------------------------------------------------------------------
  function initKankatalaCartShipping() {
    if (window.customElements.get('kankatala-shipping-bar')) return;

    class KankatalaShippingBar extends HTMLElement {
      connectedCallback() {
        this._onCartChange = (event) => {
          const cart = event.detail && event.detail.cart;
          if (!cart || !cart.items) return;

          let total = cart.items.reduce(function (sum, item) {
            return item.requires_shipping ? sum + item.final_line_price : sum;
          }, 0);

          total -= (cart.cart_level_discount_applications || []).reduce(function (sum, allocation) {
            var application = allocation.discount_application || allocation;
            return sum + (application.total_allocated_amount || 0);
          }, 0);

          this.update(total);
        };
        document.addEventListener('cart:change', this._onCartChange);
      }

      disconnectedCallback() {
        if (this._onCartChange) document.removeEventListener('cart:change', this._onCartChange);
      }

      update(totalPrice) {
        const threshold = parseInt(this.getAttribute('threshold'), 10) || 0;
        if (totalPrice < 0) totalPrice = 0;
        this.setAttribute('total-price', totalPrice);

        const fill = this.querySelector('[data-kankatala-shipping-fill]');
        if (fill) {
          const percent = threshold > 0 ? Math.min(100, (totalPrice / threshold) * 100) : 0;
          fill.style.width = percent + '%';
        }

        const message = this.querySelector('[data-kankatala-shipping-message]');
        if (!message) return;

        if (totalPrice >= threshold) {
          message.innerHTML = this.getAttribute('reached-message') || '';
        } else {
          message.innerHTML = (this.getAttribute('unreached-message') || '')
            .replace('[amount]', kankatalaFormatMoney(threshold - totalPrice));
        }
      }
    }

    window.customElements.define('kankatala-shipping-bar', KankatalaShippingBar);
  }

  // Matches how Prestige's free-shipping-bar formats its remaining amount.
  function kankatalaFormatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
    var locale = (window.Shopify && window.Shopify.locale) || 'en';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(0);
    }
  }

  // ---------------------------------------------------------------------------
  // Cart: collapsible subtotal (snippets/kankatala-cart-totals.liquid)
  //
  // One "Subtotal" button; the breakdown underneath it is hidden until tapped.
  // The open/closed choice is remembered for the session only, so every new
  // visit starts collapsed. Delegated from document so it survives the cart
  // drawer replacing its own contents on cart:change.
  // ---------------------------------------------------------------------------
  var KANKATALA_TOTALS_KEY = 'kankatala:cart-totals-open';

  // Default: closed on mobile, open on desktop. Once the shopper chooses, that
  // choice wins for the rest of the session.
  function kankatalaTotalsRemembered() {
    var stored = null;
    try {
      stored = window.sessionStorage.getItem(KANKATALA_TOTALS_KEY);
    } catch (error) {
      /* private mode: fall through to the width default */
    }
    if (stored === '1') return true;
    if (stored === '0') return false;
    return window.matchMedia('(min-width: 990px)').matches;
  }

  function kankatalaSetTotalsOpen(toggle, open) {
    var breakdown = document.getElementById(toggle.getAttribute('aria-controls'));
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (breakdown) breakdown.hidden = !open;
  }

  // The breakdown rows must reconcile to cart.total_price. If they ever stop
  // doing so, the displayed figures are wrong and that should be loud rather
  // than silent -- the Total and the Checkout label are always cart.total_price
  // regardless, so the customer is never shown a wrong amount to pay.
  function kankatalaCheckTotals() {
    var blocks = document.querySelectorAll('[data-kankatala-totals]');
    for (var i = 0; i < blocks.length; i++) {
      var expected = parseInt(blocks[i].getAttribute('data-expected-total'), 10);
      var actual = parseInt(blocks[i].getAttribute('data-cart-total'), 10);
      if (isNaN(expected) || isNaN(actual) || expected === actual) continue;
      console.warn(
        '[kankatala] Cart breakdown does not reconcile to cart.total_price. ' +
          'Rows sum to ' + expected + ' but the cart total is ' + actual + '. ' +
          'The Total and Checkout amounts are correct; the breakdown rows are not.',
        blocks[i]
      );
    }
  }

  function kankatalaAnnounceTotal() {
    var region = document.querySelector('[data-kankatala-cart-live]');
    var block = document.querySelector('[data-kankatala-totals]');
    if (!region || !block) return;
    var amount = block.querySelector('.kankatala-cart-totals__toggle-amount, .kankatala-cart-totals__total-amount');
    if (amount) region.textContent = 'Cart updated. Total ' + amount.textContent.trim() + '.';
  }

  function initKankatalaCartTotals() {
    // Re-applied on every call (not guarded) so a re-rendered drawer, whose
    // markup always comes back collapsed, picks the session state up again.
    var open = kankatalaTotalsRemembered();
    var toggles = document.querySelectorAll('[data-kankatala-totals-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      kankatalaSetTotalsOpen(toggles[i], open);
    }

    kankatalaCheckTotals();

    if (window.kankatalaCartTotalsInit) return;
    window.kankatalaCartTotalsInit = true;

    document.addEventListener('click', function (event) {
      var toggle = event.target.closest && event.target.closest('[data-kankatala-totals-toggle]');
      if (!toggle) return;
      event.preventDefault();

      var nowOpen = toggle.getAttribute('aria-expanded') !== 'true';
      try {
        window.sessionStorage.setItem(KANKATALA_TOTALS_KEY, nowOpen ? '1' : '0');
      } catch (error) {
        /* private mode: the toggle still works, it just is not remembered */
      }

      // Keep the drawer and the cart page in step when both are in the DOM
      var all = document.querySelectorAll('[data-kankatala-totals-toggle]');
      for (var i = 0; i < all.length; i++) {
        kankatalaSetTotalsOpen(all[i], nowOpen);
      }
    });

    document.addEventListener('cart:change', function () {
      window.setTimeout(function () {
        initKankatalaCartTotals();
        kankatalaAnnounceTotal();
      }, 0);
    });
  }

  // ---------------------------------------------------------------------------
  // Cart: "Add Comments" (snippets/kankatala-cart-comments.liquid)
  //
  // Writes cart.note through /cart/update.js, debounced, so typing does not
  // fire a request per keystroke. Not using Prestige's <cart-note> element,
  // which would post the same field again on `change`.
  // ---------------------------------------------------------------------------
  function initKankatalaCartComments() {
    if (window.kankatalaCartCommentsInit) return;
    window.kankatalaCartCommentsInit = true;

    var saveTimer = null;

    document.addEventListener('click', function (event) {
      var toggle = event.target.closest && event.target.closest('[data-kankatala-comments-toggle]');
      if (!toggle) return;
      event.preventDefault();

      var wrapper = toggle.closest('[data-kankatala-comments]');
      var panel = document.getElementById(toggle.getAttribute('aria-controls'));
      var open = toggle.getAttribute('aria-expanded') !== 'true';

      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (wrapper) wrapper.classList.toggle('is-expanded', open);
      if (panel) {
        panel.hidden = !open;
        if (open) {
          var field = panel.querySelector('[data-kankatala-comments-field]');
          if (field) field.focus();
        }
      }
    });

    document.addEventListener('input', function (event) {
      var field = event.target.closest && event.target.closest('[data-kankatala-comments-field]');
      if (!field) return;

      // Keep every copy of the field in step (drawer and cart page)
      var all = document.querySelectorAll('[data-kankatala-comments-field]');
      for (var i = 0; i < all.length; i++) {
        if (all[i] !== field) all[i].value = field.value;
      }

      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(function () {
        kankatalaSaveCartNote(field.value);
      }, 500);
    });

    // Don't lose an in-flight note if the page is being left
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'hidden' || !saveTimer) return;
      window.clearTimeout(saveTimer);
      saveTimer = null;
      var field = document.querySelector('[data-kankatala-comments-field]');
      if (field) kankatalaSaveCartNote(field.value, true);
    });
  }

  function kankatalaSaveCartNote(note, urgent) {
    var root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root_url + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note }),
      keepalive: urgent === true
    }).catch(function () {});
  }

  /* ------------------------------------------------------------------------
     Product cards on touch screens: show the hover image (3rd photo) while the
     card is pressed, then the tap opens the product as usual - like the
     current store. Prestige only enables the hover image for a mouse. The
     3rd image stays until the page scrolls, another card is touched or the
     product opens (a long press no longer resets it). Styles: "product
     cards: hover image on touch" in kankatala-custom.css. Delegated on
     document; binds once.
  ------------------------------------------------------------------------ */
  function initKankatalaTouchHoverImage() {
    if (window.kankatalaTouchHoverInit) return;
    window.kankatalaTouchHoverInit = true;

    var active = null;
    function clear() {
      if (active) active.classList.remove('kankatala-touch-preview');
      active = null;
    }

    document.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'touch') return;
      var figure = event.target.closest && event.target.closest('.product-card__figure');
      if (!figure || !figure.querySelector('.product-card__image--secondary')) return;
      clear();
      active = figure;
      figure.classList.add('kankatala-touch-preview');
    }, { passive: true });

    // only a real scroll resets it (not pointercancel: iOS fires that on a long press)
    var startY = 0;
    document.addEventListener('pointerdown', function () { startY = window.scrollY; }, { passive: true });
    window.addEventListener('scroll', function () {
      if (active && Math.abs(window.scrollY - startY) > 10) clear();
    }, { passive: true });
    // back/forward cache: don't come back to a card stuck on the hover image
    window.addEventListener('pageshow', clear);
  }

  // Mobile drawer menu: only one level-1 row open at a time.
  //
  // The snippet originally relied on the native exclusive-accordion
  // attribute (`<details name="kk-mobile-menu">`), but Prestige's
  // AccordionDisclosure calls preventDefault() on the summary click and then
  // sets `.open` itself, so the browser's sibling-closing never ran and two
  // rows could be open at once. Closing the sibling through its
  // accordion-disclosure element keeps Prestige's own collapse animation,
  // which setting `other.open = false` directly would skip.
  function initKankatalaMobileMenu() {
    document.querySelectorAll('.kankatala-mobile-menu').forEach(function (root) {
      if (root.dataset.kankatalaInit) return;
      root.dataset.kankatalaInit = 'true';

      // `toggle` does not bubble, so listen in the capture phase.
      root.addEventListener('toggle', function (event) {
        var details = event.target;
        if (!details || details.tagName !== 'DETAILS' || !details.open) return;

        root.querySelectorAll('details').forEach(function (other) {
          if (other === details || !other.open) return;
          // Only close siblings at the same level, never a nested details.
          if (other.contains(details) || details.contains(other)) return;

          var disclosure = other.closest('accordion-disclosure');
          if (disclosure && typeof disclosure.toggle === 'function') {
            disclosure.toggle(false);
          } else {
            other.open = false;
          }
        });
      }, true);
    });
  }

  /* ------------------------------------------------------------------------
     PLP price filter: start the price filter at the lowest price of the
     current collection view (with the active filters) instead of Prestige's
     0 - as the "from" placeholder, the "from" field's minimum and the
     slider's left end. Prestige draws the slider fill assuming it starts at
     0, so the fill is recalculated here after Prestige's own handlers. Shopify's Liquid filter object only knows the maximum,
     so the minimum comes from sections/kankatala-price-min.liquid via the
     Section Rendering API (sorted by price, 1 product). One request per
     filter combination, cached. Re-applied when Prestige or the drawer
     live-update re-render the filters.
  ------------------------------------------------------------------------ */
  function initKankatalaPriceMin() {
    if (window.kankatalaPriceMinInit) return;
    window.kankatalaPriceMinInit = true;

    var cache = {};
    var scheduled = false;

    function apply() {
      scheduled = false;
      if (window.location.pathname.indexOf('/collections/') === -1) return;
      var inputs = document.querySelectorAll('price-range input[name="filter.v.price.gte"]:not([data-kankatala-price-min])');
      if (!inputs.length) return;

      var params = new URLSearchParams(window.location.search);
      ['page', 'sort_by', 'filter.v.price.gte', 'filter.v.price.lte'].forEach(function (key) { params.delete(key); });
      // "In stock" may be applied but hidden from the URL (initKankatalaDefaultInStock)
      if (!params.has('filter.v.availability') && document.querySelector('facets-form input[name="filter.v.availability"][value="1"]:checked')) {
        params.set('filter.v.availability', '1');
      }
      params.set('sort_by', 'price-ascending');
      params.set('section_id', 'kankatala-price-min');
      var url = window.location.pathname + '?' + params.toString();

      inputs.forEach(function (input) { input.setAttribute('data-kankatala-price-min', ''); });

      cache[url] = cache[url] || fetch(url)
        .then(function (response) { return response.ok ? response.text() : ''; })
        .then(function (html) {
          var match = html.match(/data-kankatala-price-min="(\d+)"/);
          return match ? Math.floor(parseInt(match[1], 10) / 100) : null;
        })
        .catch(function () { return null; });

      cache[url].then(function (lowest) {
        if (lowest === null) return;
        inputs.forEach(function (input) {
          input.placeholder = String(lowest);
          input.min = String(lowest);
          input.setAttribute('data-kankatala-price-min', String(lowest));
          startSliderAt(input.closest('price-range'), lowest);
        });
      });
    }

    function startSliderAt(priceRange, lowest) {
      if (!priceRange) return;
      var lower = priceRange.querySelector('input[type="range"]:first-child');
      var higher = priceRange.querySelector('input[type="range"]:last-child');
      if (!lower || !higher) return;
      var max = parseInt(lower.max, 10);
      if (!(lowest < max)) return;

      lower.min = String(lowest);   // also moves a 0 value up to the lowest price
      higher.min = String(lowest);

      function percent(value) {
        return ((parseInt(value, 10) - lowest) / (max - lowest)) * 100 + '%';
      }
      function syncFill() {
        lower.parentElement.style.setProperty('--range-min', percent(lower.value));
        lower.parentElement.style.setProperty('--range-max', percent(higher.value));
      }

      if (!priceRange.hasAttribute('data-kankatala-range-sync')) {
        priceRange.setAttribute('data-kankatala-range-sync', '');
        // registered after Prestige's listeners, so these run last
        priceRange.querySelectorAll('input').forEach(function (input) {
          input.addEventListener('input', syncFill);
          input.addEventListener('change', syncFill);
        });
      }
      syncFill();
    }

    apply();
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(apply);
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Mega panel "list with live preview" (Occasion).
  //
  // The list rows and the stage frames are both real links rendered by
  // snippets/kankatala-mega-menu.liquid, so the panel works with JS off: the
  // first picture is shown and every row navigates. This only swaps which
  // frame is visible as the pointer or keyboard moves down the list.
  //
  // Pointer leaving the list is deliberately NOT handled: the brief asks for
  // the last picture to stay while the mouse travels into the image.
  function initKankatalaMegaPreview() {
    document.querySelectorAll('[data-kankatala-preview]').forEach(function (root) {
      if (root.dataset.kankatalaInit) return;
      root.dataset.kankatalaInit = 'true';

      var links = Array.prototype.slice.call(
        root.querySelectorAll('[data-kankatala-preview-link]')
      );
      var frames = Array.prototype.slice.call(
        root.querySelectorAll('[data-kankatala-preview-frame]')
      );
      if (links.length === 0 || frames.length === 0) return;

      function activate(index) {
        links.forEach(function (link, i) {
          link.classList.toggle('is-active', i === index);
        });
        frames.forEach(function (frame, i) {
          var on = i === index;
          frame.classList.toggle('is-active', on);
          if (on) {
            frame.removeAttribute('aria-hidden');
            frame.removeAttribute('tabindex');
          } else {
            frame.setAttribute('aria-hidden', 'true');
            frame.setAttribute('tabindex', '-1');
          }
        });
      }

      links.forEach(function (link, index) {
        link.addEventListener('mouseenter', function () {
          activate(index);
        });
        link.addEventListener('focus', function () {
          activate(index);
        });

        link.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowDown') next = index + 1;
          if (event.key === 'ArrowUp') next = index - 1;
          if (next === null) return;
          if (next < 0 || next >= links.length) return;

          event.preventDefault();
          links[next].focus();
        });
      });
    });
  }

  // BUCKS currency converter: re-convert after the theme re-renders prices.
  //
  // BUCKS converts the text of `.price--end, .money, .wizzy-product-item-price,
  // .max-price_bubble` once, then again on its configured triggers. Prestige
  // swaps whole chunks of DOM for variant changes, cart updates and facets,
  // and the app's triggers do not cover those, so the new markup would be
  // left in rupees. Calling `rerender()` on Prestige's own events covers
  // every case without touching the BUCKS dashboard.
  //
  // Debounced because `cart:change` and `cart-drawer:refreshed` fire together.
  var kkBucksTimer = null;

  function kankatalaBucksRerender() {
    if (kkBucksTimer) clearTimeout(kkBucksTimer);
    kkBucksTimer = setTimeout(function () {
      kkBucksTimer = null;
      var cc = window.bucksCC;
      if (!cc) return;

      // Verified against the live SDK (/apps/buckscc/sdk.min.js): it exports
      // BOTH `reConvert` and `rerender`. They are not interchangeable.
      //   reConvert() - re-reads the price selectors and converts the text.
      //                 This is what new markup needs.
      //   rerender()  - rebuilds the selector widget itself. Only needed when
      //                 a mount point appears late, i.e. the mobile drawer.
      // The app embed defines `reConvert` as a no-op placeholder before the
      // SDK loads, so calling it early is harmless.
      if (typeof cc.reConvert === 'function') cc.reConvert();

      // Mobile drawer: BUCKS is injected BEFORE `.mobile-buck-app`, so if the
      // marker has no preceding box the widget has not been mounted yet.
      var marker = document.querySelector('.kankatala-mobile-menu__tiles .mobile-buck-app');
      if (marker && !marker.previousElementSibling && typeof cc.rerender === 'function') {
        cc.rerender();
      }
    }, 60);
  }

  // Exposed so the page-specific bundles (kankatala-pdp.js,
  // kankatala-wishlist-page.js) can ask for a re-convert after they write a
  // price, without each of them re-implementing the guard.
  window.kankatalaBucksRerender = kankatalaBucksRerender;

  function initKankatalaBucks() {
    if (document.documentElement.dataset.kankatalaBucksInit) return;
    document.documentElement.dataset.kankatalaBucksInit = 'true';

    [
      'variant:change',
      'product:rerender',
      'cart:change',
      'cart:refresh',
      'cart-drawer:refreshed',
      'facet:update',
      'line-item:change'
    ].forEach(function (name) {
      document.addEventListener(name, kankatalaBucksRerender);
    });
  }

  // Drawer height from the VISUAL viewport.
  //
  // On iOS Safari `100dvh` is the LARGE viewport: with the toolbar collapsed
  // the drawer runs underneath it. The content is visible, but iOS reserves
  // touches in that band for Safari, so the lower part of the footer tiles
  // did nothing. `visualViewport.height` is the part actually on screen.
  //
  // Only the custom property is written - no layout is read - so this cannot
  // thrash. Browsers without visualViewport keep the dvh fallback.
  function initKankatalaDrawerViewport() {
    var vv = window.visualViewport;
    if (!vv) return;
    if (document.documentElement.dataset.kankatalaDrawerVp) return;
    document.documentElement.dataset.kankatalaDrawerVp = 'true';

    function sync() {
      document.documentElement.style.setProperty('--kk-drawer-h', vv.height + 'px');
    }

    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    window.addEventListener('orientationchange', sync);
    // The drawer can open after a toolbar collapse that fired no event.
    document.addEventListener('dialog:after-show', sync);
  }

  function initAll() {
    initKankatalaDrawerViewport();
    initKankatalaBucks();
    initKankatalaMobileMenu();
    initKankatalaMegaPreview();
    kankatalaBucksRerender();
    initKankatalaAutoplayCarousels();
    initKankatalaStores();
    initKankatalaLuxuryGrid();
    initKankatalaOccasionSplide();
    initKankatalaCustomGridLazyImages();
    initKankatalaTestimonials();
    initKankatalaSpecificInformation();
    initKankatalaCollectionsSplide();
    initKankatalaProducts();
    initKankatalaJourney();
    initKankatalaDrawerFacets();
    initKankatalaTailoring();
    initKankatalaTailoringCartSync();
    initKankatalaTouchHoverImage();
    initKankatalaPriceMin();
    initKankatalaCartAddons();
    initKankatalaCartShipping();
    initKankatalaCartTotals();
    initKankatalaCartComments();
  }

  // Page load only (not section:load) so the theme editor isn't redirected.
  ready(initKankatalaDefaultInStock);

  ready(initAll);

  // Shopify's theme editor replaces a section's entire HTML via AJAX
  // whenever a setting changes (not a full page reload), which fires this
  // event with the fresh DOM already in place. Without listening for it,
  // none of the above would ever run again after the very first page load —
  // e.g. switching "Items per row (mobile)" in the editor wouldn't visibly
  // do anything, since the newly-rendered elements never get initialized.
  document.addEventListener('shopify:section:load', initAll);
})();